import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EventLink } from '../../components/EventLink';
import { api } from '../../api/client';
import type { CarouselEvent } from '../../types';
import { format } from 'date-fns';
import { parseEventDate } from '../../utils/eventDate';

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [input, setInput] = useState(q);
  const [results, setResults] = useState<CarouselEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setSearched(false); return; }
    setLoading(true);
    setSearched(true);
    api.get<{ events: CarouselEvent[] }>(`/events/search?q=${encodeURIComponent(q)}`)
      .then(r => setResults(r.data.events))
      .finally(() => setLoading(false));
  }, [q]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (input.trim()) setSearchParams({ q: input.trim() });
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-6">Search</h1>
      <form onSubmit={handleSubmit} className="flex gap-2 mb-8">
        <input
          type="search"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Search events, places, people…"
          autoFocus
          className="flex-1 border border-stone-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          type="submit"
          className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors"
        >
          Search
        </button>
      </form>

      {loading && <p className="text-slate-400 text-sm">Searching…</p>}

      {!loading && searched && results.length === 0 && (
        <p className="text-slate-400 text-sm">No results for "{q}".</p>
      )}

      {!loading && results.length > 0 && (
        <div className="space-y-3" data-event-nav-scope>
          <p className="text-xs text-slate-400 font-medium">{results.length} result{results.length !== 1 ? 's' : ''}</p>
          {results.map(event => {
            const photo = event.photos?.at(0);
            const dateStr = event.eventDate ? format(parseEventDate(event.eventDate), 'MMM d, yyyy') : null;
            return (
              <EventLink
                key={event.id}
                eventId={event.id} title={event.title}
                className="flex gap-3 bg-white rounded-xl shadow-sm border border-slate-100 p-3 hover:shadow-md hover:border-indigo-100 transition-all group"
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                  {photo
                    ? <img src={photo.filePath} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" style={{ objectPosition: '50% 25%' }} />
                    : <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                        </svg>
                      </div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{event.title}</p>
                  <div className="flex gap-3 mt-0.5 text-xs text-slate-400">
                    {dateStr && <span>{dateStr}</span>}
                    {event.location && <span>{event.location}</span>}
                  </div>
                  {(event.photos?.length > 0 || event.audioClips?.length > 0) && (
                    <div className="flex gap-1.5 mt-1.5">
                      {event.photos?.length > 0 && (
                        <span className="text-[0.65rem] font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">
                          {event.photos.length} photo{event.photos.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {event.audioClips?.length > 0 && (
                        <span className="text-[0.65rem] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                          ♪ {event.audioClips.length}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </EventLink>
            );
          })}
        </div>
      )}
    </div>
  );
}
